import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Clock, Calendar, MapPin } from 'lucide-react';
import axios from 'axios';
import SignatureCanvas from '../components/AttendanceEvents/SignatureCanvas';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// A thin standalone axios for unauthenticated calls (external flow).
// Falls back to same-origin if REACT_APP_API_URL is not set.
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  timeout: 20000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// Compact 12hr time: "2pm", "2:30pm".
const formatShortTime = (date) => {
  const h = date.getHours();
  const m = date.getMinutes();
  const meridiem = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${meridiem}` : `${h12}:${String(m).padStart(2, '0')}${meridiem}`;
};

// Compress a signature data URL by re-encoding via an offscreen canvas.
// Typical output is 8-20KB PNG which is well within upload budget for concurrent scans.
const compressSignatureDataUrl = async (dataUrl, { maxWidth = 600, maxHeight = 180 } = {}) => {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch (_) {
      resolve(dataUrl);
    }
  });
};

// Page wrapper. The app shell locks document scrolling globally, so public
// attendee pages must provide their own viewport-height scroll container.
// `withStickyBar` reserves bottom padding equivalent to the fixed action bar
// height (~64px) plus the iPhone safe-area inset (~34pt) so the last form
// element doesn't get hidden behind the bar. Other screens (success, error,
// choose, alreadyCheckedIn) render no bar and keep the original tighter footer.
const Screen = ({ children, withStickyBar = false }) => (
  <div
    className={
      `h-screen overflow-y-auto bg-gradient-to-b from-slate-50 to-slate-100 flex items-start justify-center px-4 pt-4 ` +
      (withStickyBar ? 'pb-32' : 'pb-4')
    }
    style={{ height: '100dvh', WebkitOverflowScrolling: 'touch' }}
  >
    <div className="w-full max-w-lg">{children}</div>
  </div>
);

// Fixed bottom action bar. Pinned to the viewport bottom regardless of
// scroll position so the primary CTA is always findable on mobile —
// external-attendee feedback was that the "Check in" button was below
// the fold (after the signature pad + PDPA consent box) and people
// thought the form had no submit button at all.
//
// Why position:fixed (not sticky):
//   The body itself scrolls; sticky requires a scrollable parent and
//   only triggers within the parent's bounds. For "always visible at
//   the bottom of the viewport regardless of scroll", fixed is correct.
//
// Why max-w-lg inside:
//   On wide screens the bar stretches edge-to-edge (matches the
//   industry convention — Stripe Checkout, Grab, Apple Pay, Shopee)
//   while the button itself stays the same width as the form column
//   above it, so it doesn't look orphaned on a 1080p monitor.
//
// safe-area-inset-bottom:
//   iPhones with a home indicator reserve ~34pt at the bottom; without
//   the inset the button label sits on top of the indicator and looks
//   broken. env(safe-area-inset-bottom) resolves to 0 on browsers that
//   don't support it, so the fallback is benign.
//
// backdrop-blur + translucent white:
//   Looks sharp when the page content scrolls underneath, and avoids
//   the harsh "two-tone document" feel of a flat opaque bar.
const StickyActionBar = ({ children }) => (
  <div
    className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 pt-3 z-30 shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.08)]"
    style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
  >
    <div className="max-w-lg mx-auto">{children}</div>
  </div>
);

// Compact event summary shown above the check-in form. Date/time on one line,
// location on the next — keeping them on separate rows keeps each fact
// scannable and leaves room for long venue names without pushing the date
// off-screen on narrow phones.
const EventHeader = ({ event }) => (
  <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
    <div className="flex items-center gap-2 flex-wrap mb-1.5">
      <div className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 uppercase tracking-wider">
        {event.event_type}
      </div>
      {event.reference_no && (
        <span className="text-[11px] font-mono text-gray-600 tracking-wider bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
          {event.reference_no}
        </span>
      )}
    </div>
    <h1 className="text-lg md:text-xl font-bold text-gray-900 leading-tight">{event.title}</h1>
    <div className="mt-2 space-y-1 text-sm text-gray-600">
      <p className="inline-flex items-center gap-1.5">
        <Calendar className="w-4 h-4 flex-shrink-0" />
        {new Date(event.start_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
        {' · '}
        {formatShortTime(new Date(event.start_at))}
      </p>
      {event.location && (
        <p className="inline-flex items-center gap-1.5">
          <MapPin className="w-4 h-4 flex-shrink-0" />{event.location}
        </p>
      )}
      {event.project?.project_code && (
        <p className="inline-flex items-center gap-1.5 text-xs text-blue-700">
          <span className="font-mono font-semibold">{event.project.project_code}</span>
          <span className="text-gray-600">· {event.project.project_name}</span>
        </p>
      )}
    </div>
  </div>
);

const ErrorScreen = ({ title, message }) => (
  <Screen>
    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-7 h-7 text-red-600" />
      </div>
      <h1 className="text-lg font-semibold text-gray-900 mb-2">{title}</h1>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  </Screen>
);

const SuccessScreen = ({ event, status, externalModerationRequired }) => {
  const pending = status === 'pending_approval';
  return (
    <Screen>
      <EventHeader event={event} />
      <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${pending ? 'bg-orange-100' : 'bg-green-100'}`}>
          {pending ? (
            <Clock className="w-8 h-8 text-orange-600" />
          ) : (
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          )}
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {pending ? 'Submitted — awaiting approval' : "You're checked in"}
        </h2>
        <p className="text-sm text-gray-600 max-w-sm mx-auto">
          {pending
            ? 'The host will review and approve your check-in. You do not need to scan again.'
            : 'Thanks for attending. You can close this page.'}
        </p>
      </div>
    </Screen>
  );
};

const InternalForm = ({ event, me, sessionToken, onSuccess, requiresSignature }) => {
  const sigRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (requiresSignature && !hasInk) {
      setError('Please sign before submitting.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      let sig = '';
      if (hasInk) {
        sig = sigRef.current.toDataURL();
        sig = await compressSignatureDataUrl(sig);
      }
      // session_token was minted by the verify endpoint; the URL (scan)
      // token is not used here. See qr_token_service for the two-token
      // design rationale.
      const { data } = await api.post('/attendance-events/public/checkin/internal/', {
        session_token: sessionToken,
        signature_base64: sig,
      });
      onSuccess(data.status);
    } catch (err) {
      setError(err?.response?.data?.error || 'Check-in failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <Screen withStickyBar>
        <EventHeader event={event} />

        {/* Identity card — no "YOUR DETAILS" heading (context is obvious: the
            user just scanned a QR), no work email (redundant for attendance
            capture; the staff_id is the canonical employee identifier we'll
            look them up by). Name + staff_id live on the same line so the
            designation gets a line of its own without pushing the card tall. */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
          <p className="text-sm font-semibold text-gray-900">
            {(() => {
              const fullName = [me.first_name, me.last_name].filter(Boolean).join(' ');
              // Only append "· staff_id" when we actually have a name to prefix
              // with — otherwise we'd render "S0102 · S0102" for the edge case
              // where the backend returns no first/last name.
              return fullName ? (
                <>
                  {fullName}
                  <span className="ml-1.5 text-gray-500 font-normal">· {me.staff_id}</span>
                </>
              ) : me.staff_id;
            })()}
          </p>
          {me.designation && (
            <p className="text-xs text-gray-600 mt-0.5">{me.designation}</p>
          )}
        </div>

        {requiresSignature && (
          <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
            <label className="block text-sm font-semibold text-gray-900 mb-2">Signature <span className="text-red-500">*</span></label>
            <SignatureCanvas ref={sigRef} onChange={setHasInk} height={160} />
          </div>
        )}

        {/* Errors stay in-flow rather than inside the sticky bar so users
            see the message attached to the form context that produced it.
            The sticky bar's job is the primary action only. */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-1">
          By checking in you confirm your attendance at this event.
        </p>
      </Screen>

      <StickyActionBar>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="btn btn-primary btn-lg w-full"
        >
          {submitting ? 'Checking in…' : 'Check in'}
        </button>
      </StickyActionBar>
    </>
  );
};

const ExternalForm = ({ event, sessionToken, consentNotice, consentVersion, onSuccess, requiresSignature }) => {
  const sigRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [designation, setDesignation] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);

  // Required-field policy mirrors backend serializer:
  // - name, company, designation: required (designation is the disambiguator
  //   for 7-year-old audit lookups, e.g. "John Tan, Acme, Project Manager")
  // - email: optional (site workers on personal phones often don't have a
  //   corporate email handy; forcing it kills check-in conversion)
  const canSubmit = name.trim() && company.trim() && designation.trim() && consent && (!requiresSignature || hasInk);

  const submit = async () => {
    if (!canSubmit) {
      setError('Please complete all required fields and acknowledge the consent notice.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      let sig = '';
      if (hasInk) {
        sig = sigRef.current.toDataURL();
        sig = await compressSignatureDataUrl(sig);
      }
      // session_token was minted by the verify endpoint; the URL (scan)
      // token is not used here. See qr_token_service for the two-token
      // design rationale.
      const { data } = await publicApi.post('/attendance-events/public/checkin/external/', {
        session_token: sessionToken,
        name: name.trim(),
        company: company.trim(),
        designation: designation.trim(),
        email: email.trim(),
        signature_base64: sig,
        consent_acknowledged: consent,
      });
      onSuccess(data.status);
    } catch (err) {
      setError(err?.response?.data?.error || 'Check-in failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <Screen withStickyBar>
        <EventHeader event={event} />

        {/* Identity card — heading dropped (mirrors the internal card pattern;
            context is obvious on a phone). Tighter vertical rhythm: space-y-2.5
            between fields, label-to-input gap reduced. */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-3 space-y-2.5">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Full name <span className="text-red-500">*</span></label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)} required maxLength={150}
              autoComplete="name"
              className="input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Company <span className="text-red-500">*</span></label>
            <input
              type="text" value={company} onChange={e => setCompany(e.target.value)} required maxLength={200}
              autoComplete="organization"
              className="input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Designation <span className="text-red-500">*</span></label>
            <input
              type="text" value={designation} onChange={e => setDesignation(e.target.value)} required maxLength={150}
              autoComplete="organization-title"
              className="input"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              inputMode="email" autoComplete="email" maxLength={254}
              className="input"
            />
          </div>
        </div>

        {requiresSignature && (
          <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
            <label className="block text-sm font-semibold text-gray-900 mb-2">Signature <span className="text-red-500">*</span></label>
            <SignatureCanvas ref={sigRef} onChange={setHasInk} height={160} />
          </div>
        )}

        {/* PDPA card compressed: notice text lives inside a <details> element
            so the wall of legal copy is collapsed by default. The checkbox +
            version label are always visible, giving us legal coverage without
            burning ~100px of mobile screen on copy nobody reads carefully.
            The full notice remains one tap away — required for PDPA "informed
            consent" where the data subject must be able to access the notice. */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-900 leading-snug">
              I consent to the collection of my personal data per the{' '}
              <details className="inline group">
                <summary className="inline cursor-pointer text-blue-800 underline decoration-dotted underline-offset-2 list-none">
                  PDPA notice
                </summary>
                <span className="block mt-2 text-xs text-gray-600 leading-relaxed">{consentNotice}</span>
              </details>
              .
              <span className="text-xs text-gray-500 block mt-1">Consent version: {consentVersion}</span>
            </span>
          </label>
        </div>

        {/* Errors stay in-flow rather than inside the sticky bar so users
            see the message attached to the form context that produced it
            (e.g. "Please tick the consent box" appears next to the consent
            box, not floating at the bottom of the screen). */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </Screen>

      <StickyActionBar>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="btn btn-primary btn-lg w-full"
        >
          {submitting ? 'Submitting…' : 'Check in'}
        </button>
      </StickyActionBar>
    </>
  );
};

const AttendEvent = () => {
  // The `token` in the URL is the SCAN token (embedded in the QR). It's only
  // used for the initial verify call; after that, the backend hands us a
  // session token that authenticates the actual check-in POST.
  const { token: scanToken } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [me, setMe] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [consentNotice, setConsentNotice] = useState('');
  const [consentVersion, setConsentVersion] = useState('');
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState(null);
  const [externalModerationRequired, setExternalModerationRequired] = useState(false);
  const [mode, setMode] = useState(null); // 'internal' | 'external' | 'choose'

  // Fetch token metadata. Try authenticated first so internal employees get
  // their identity. On success, the response includes a single-use session
  // token that we'll send with the check-in POST — see qr_token_service.
  useEffect(() => {
    if (authLoading) return;
    let active = true;
    const load = async () => {
      try {
        const client = user ? api : publicApi;
        const { data } = await client.get(`/attendance-events/public/verify/${encodeURIComponent(scanToken)}/`);
        if (!active) return;
        setEvent(data.event);
        setMe(data.me);
        setSessionToken(data.session_token || null);
        setConsentNotice(data.consent_notice);
        setConsentVersion(data.consent_notice_version);
        setAlreadyCheckedIn(!!data.already_checked_in);
        setExternalModerationRequired(!!data.event.external_moderation_required);
        if (data.me) {
          setMode('internal');
        } else if (data.event.allow_external) {
          setMode('choose');
        } else {
          setMode('internal'); // triggers login redirect below
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error || 'This check-in link is invalid or has expired.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [scanToken, user, authLoading]);

  if (authLoading || loading) {
    return (
      <Screen>
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">Loading…</div>
      </Screen>
    );
  }

  if (error) {
    return <ErrorScreen title="Unable to check in" message={error} />;
  }

  if (submittedStatus) {
    return <SuccessScreen event={event} status={submittedStatus} externalModerationRequired={externalModerationRequired} />;
  }

  if (alreadyCheckedIn) {
    return (
      <Screen>
        <EventHeader event={event} />
        <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-7 h-7 text-blue-800" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">You&apos;re already checked in</h2>
          <p className="text-sm text-gray-600">No need to scan again. You can close this page.</p>
        </div>
      </Screen>
    );
  }

  if (mode === 'choose') {
    return (
      <Screen>
        <EventHeader event={event} />
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">How are you attending?</h2>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                // Preserve the current attend URL as the post-login target
                const next = encodeURIComponent(window.location.pathname);
                navigate(`/login?next=${next}`);
              }}
              className="w-full text-left h-auto p-4 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <p className="font-semibold text-gray-900">I&apos;m a Winner Engineering employee</p>
              <p className="text-xs text-gray-600 mt-0.5">Sign in with your HR Portal account — your details are prefilled.</p>
            </button>
            <button
              type="button"
              onClick={() => setMode('external')}
              className="w-full text-left h-auto p-4 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <p className="font-semibold text-gray-900">I&apos;m an external attendee</p>
              <p className="text-xs text-gray-600 mt-0.5">Contractor, client, auditor, guest — no login required.</p>
            </button>
          </div>
        </div>
      </Screen>
    );
  }

  if (mode === 'internal' && !user) {
    // Force-login path per policy: no OTP alternative.
    const next = encodeURIComponent(window.location.pathname);
    navigate(`/login?next=${next}`);
    return null;
  }

  if (mode === 'internal' && me) {
    return (
      <InternalForm
        event={event}
        me={me}
        sessionToken={sessionToken}
        requiresSignature={event.requires_signature}
        onSuccess={(status) => setSubmittedStatus(status)}
      />
    );
  }

  if (mode === 'external') {
    return (
      <ExternalForm
        event={event}
        sessionToken={sessionToken}
        consentNotice={consentNotice}
        consentVersion={consentVersion}
        requiresSignature={event.requires_signature}
        onSuccess={(status) => setSubmittedStatus(status)}
      />
    );
  }

  return <ErrorScreen title="Unable to continue" message="Unexpected state. Please rescan the QR code." />;
};

export default AttendEvent;
